import Close from "@mui/icons-material/Close";
import { FormControl, MenuItem, TextField } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ChangeEvent, FormEvent, useCallback, useState } from "react";
import { useUpdatePatientWeightMutation } from "src/api/services/weightTrackerApi";
import { PatientWeight } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import { heights } from "src/constants/heights";
import { isError } from "src/utils/isError";
import { desktopSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: () => void;
  vitals: PatientWeight;
}

const UpdateVitalsModal = ({ open, onClose, vitals }: Props) => {
  const [error, setError] = useState("");
  const [updateWeight, { isLoading }] = useUpdatePatientWeightMutation();
  const [feet, setFeet] = useState(Math.floor(vitals.height / 12).toString());
  const [inch, setInch] = useState((vitals.height % 12).toString());
  const [pounds, setPounds] = useState(vitals.weight.toString());
  const [goal, setGoal] = useState(vitals.goal?.toString());

  const handleFeet = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFeet(event.target.value);
  }, []);

  const handleInch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setInch(event.target.value);
  }, []);

  const handleWeight = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setPounds(event.target.value);
  }, []);

  const handleGoal = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setGoal(event.target.value);
  }, []);

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setError("");

      const heightInInches = Number(feet) * 12 + Number(inch);

      updateWeight({
        date: vitals.date,
        id: vitals.id,
        patient_id: vitals.patient_id,
        height: heightInInches,
        weight: Number(pounds),
        goal: Number(goal),
      })
        .unwrap()
        .catch((err) => {
          if (isError(err)) {
            setError(err.data.message);
          }
        })
        .then(() => {
          onClose();
        });
    },
    [
      feet,
      goal,
      inch,
      onClose,
      pounds,
      updateWeight,
      vitals.date,
      vitals.id,
      vitals.patient_id,
    ]
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={4} component="form" onSubmit={onSubmit}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            Update vitals:
          </Typography>
          <IconButton onClick={onClose}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack gap={2}>
          <Stack direction="row" gap={2}>
            <FormControl fullWidth>
              <TextField
                select
                required
                label="Height (ft)"
                value={feet}
                onChange={handleFeet}
              >
                {heights.map((num) => (
                  <MenuItem key={`feet-${num}`} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
            <FormControl fullWidth>
              <TextField
                select
                required
                label="Height (in)"
                value={inch}
                onChange={handleInch}
              >
                {heights.map((num) => (
                  <MenuItem key={`inch-${num}`} value={num}>
                    {num}
                  </MenuItem>
                ))}
              </TextField>
            </FormControl>
          </Stack>
          <FormControl>
            <TextField
              type="number"
              required
              label="Weight (lb)"
              value={pounds}
              onChange={handleWeight}
            />
          </FormControl>
          <FormControl>
            <TextField
              type="number"
              required
              label="Goal (lb)"
              value={goal}
              onChange={handleGoal}
            />
          </FormControl>
        </Stack>

        <Stack gap={1}>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error}
            </Typography>
          ) : null}
          <LoadingButton loading={isLoading} type="submit">
            Update
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default UpdateVitalsModal;
